import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import './Partners.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    discord_url: '',
    display_order: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partners/admin/list`, {
        withCredentials: true
      });
      setPartners(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Failed to load partners');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await axios.put(`${API_URL}/api/partners/${editingPartner.id}`, formData, {
          withCredentials: true
        });
        toast.success('Partner updated successfully');
      } else {
        await axios.post(`${API_URL}/api/partners`, formData, {
          withCredentials: true
        });
        toast.success('Partner created successfully');
      }
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      description: partner.description || '',
      logo: partner.logo || '',
      discord_url: partner.discord_url || '',
      display_order: partner.display_order || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/partners/${id}`, {
        withCredentials: true
      });
      toast.success('Partner deleted successfully');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo: '',
      discord_url: '',
      display_order: 0
    });
    setEditingPartner(null);
    setShowModal(false);
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-partners">
      <div className="admin-header">
        <h1>Partners Management</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <span>+</span> Add Partner
        </button>
      </div>

      <div className="partners-list">
        {partners.length === 0 ? (
          <div className="empty-state">
            <p>No partners yet. Add your first partner!</p>
          </div>
        ) : (
          <div className="partners-grid-admin">
            {partners.map((partner) => (
              <div key={partner.id} className="partner-card-admin">
                <div className="partner-logo-admin">
                  {partner.logo ? (
                    <img src={partner.logo} alt={partner.name} />
                  ) : (
                    <div className="partner-logo-placeholder-admin">
                      {partner.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="partner-details">
                  <h3>{partner.name}</h3>
                  <p className="partner-description-admin">{partner.description || 'No description'}</p>
                  {partner.discord_url && (
                    <a href={partner.discord_url} target="_blank" rel="noopener noreferrer" className="partner-link">
                      Discord Server
                    </a>
                  )}
                  <div className="partner-meta">
                    <span className="partner-order">Order: {partner.display_order}</span>
                  </div>
                </div>
                <div className="partner-actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(partner)}>
                    Edit
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(partner.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Partner Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  placeholder="Brief description of the partner..."
                />
              </div>

              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="url"
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
                {formData.logo && (
                  <div className="logo-preview">
                    <img src={formData.logo} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Discord Server URL</label>
                <input
                  type="url"
                  value={formData.discord_url}
                  onChange={(e) => setFormData({ ...formData, discord_url: e.target.value })}
                  placeholder="https://discord.gg/..."
                />
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  min="0"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success">
                  {editingPartner ? 'Update Partner' : 'Create Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Partners;
